import { eventChain } from '@app/components/common/EventChainProvider'
import { CurrentUserAssessmentState } from '@app/storage/currentUserAssessment'
import { AssessmentEvent, AssessmentActions } from '@app/firebase/api/types'
import { shouldSkipAssessmentItemViewEvent, shouldSkipReviewEvent } from './dedupState'

const ACTION_MAP: Record<AssessmentActions, string> = {
  [AssessmentActions.started]: 'start',
  [AssessmentActions.answered]: 'answer',
  [AssessmentActions.paused]: 'pause',
  [AssessmentActions.resumed]: 'resume',
  [AssessmentActions.completed]: 'finish',
  [AssessmentActions.submitted]: 'submit',
  [AssessmentActions.restarted]: 'restart',
  [AssessmentActions.reset]: 'reset',
  [AssessmentActions.skipped]: 'skip',
  [AssessmentActions.timeAdded]: 'time_added',
  [AssessmentActions.sync]: 'sync',
}

type ReviewEventInput = {
  contentId?: string | null
  courseCategory?: string | null
  districtId?: string | null
  organizationId?: string | null
  classId?: string | null
  schoolId?: string | null
  teacherId?: string | null
  origin?: string | null
  preview?: boolean
  questionPreviewId?: string | null
}

type AssessmentItemViewEventInput = ReviewEventInput & {
  itemId?: string | null
}

const compactMeta = (data: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined && value !== null)
  )

const getReviewEventKey = (payload: ReviewEventInput): string =>
  [
    CurrentUserAssessmentState.assessmentId,
    CurrentUserAssessmentState.program,
    CurrentUserAssessmentState.subject,
    payload.contentId,
    payload.classId,
    payload.schoolId,
    payload.districtId,
    payload.organizationId,
    payload.teacherId,
    payload.origin,
    payload.preview,
    payload.questionPreviewId,
  ]
    .map(value => String(value ?? ''))
    .join('|')

const getAssessmentItemViewEventKey = (payload: AssessmentItemViewEventInput): string =>
  [
    CurrentUserAssessmentState.assessmentId,
    CurrentUserAssessmentState.program,
    CurrentUserAssessmentState.subject,
    payload.contentId,
    payload.itemId,
    payload.classId,
    payload.schoolId,
    payload.districtId,
    payload.organizationId,
    payload.teacherId,
    payload.origin,
    payload.preview,
    payload.questionPreviewId,
  ]
    .map(value => String(value ?? ''))
    .join('|')

const getReviewEventBase = (payload: ReviewEventInput) => ({
  label: 'reporting',
  contentType: 'TEST',
  contentRefId: CurrentUserAssessmentState.assessmentId,
  program: CurrentUserAssessmentState.program,
  subject: CurrentUserAssessmentState.subject,
  url: window.location.href,
  meta: compactMeta(payload),
})

export const trackEvent = (
  payload: Pick<AssessmentEvent, 'action' | 'type' | 'data' | 'passedTime'>
): void => {
  // Queue for PostgreSQL via event chain (no-op if provider is unmounted).
  // Wrapped defensively — telemetry must never break the assessment flow.
  try {
    eventChain.trackEvent({
      type: ACTION_MAP[payload.action],
      category: 'assessment',
      contentType: 'TEST',
      contentRefId: CurrentUserAssessmentState.assessmentId,
      program: CurrentUserAssessmentState.program,
      subject: CurrentUserAssessmentState.subject,
      timeSpent: payload.passedTime,
      url: window.location.href,
      meta: payload.data,
    }, payload.passedTime)
  } catch {
    // Telemetry must never break assessment flow
  }
}

export const trackReviewEvent = (payload: ReviewEventInput): void => {
  try {
    const reviewEventKey = getReviewEventKey(payload)
    if (shouldSkipReviewEvent(reviewEventKey)) {
      return
    }

    const eventBase = getReviewEventBase(payload)

    eventChain.trackEvent({
      type: 'view',
      category: 'navigation',
      ...eventBase,
    }, 0)
    eventChain.trackEvent({
      type: payload.preview ? 'preview' : 'review',
      category: 'assessment',
      ...eventBase,
    }, 0)
  } catch {
    // Telemetry must never break review flow
  }
}

export const trackAssessmentItemViewEvent = (payload: AssessmentItemViewEventInput): void => {
  try {
    if (!payload.itemId) {
      return
    }

    const itemViewEventKey = getAssessmentItemViewEventKey(payload)
    if (shouldSkipAssessmentItemViewEvent(itemViewEventKey)) {
      return
    }

    eventChain.trackEvent({
      type: 'view',
      category: 'navigation',
      label: 'reporting',
      contentType: 'TEST_ITEM',
      contentRefId: payload.itemId,
      program: CurrentUserAssessmentState.program,
      subject: CurrentUserAssessmentState.subject,
      url: window.location.href,
      meta: compactMeta({
        ...payload,
        assessmentId: CurrentUserAssessmentState.assessmentId,
      }),
    }, 0)
  } catch {
    // Telemetry must never break review flow
  }
}
